import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { useLangStore } from "@/lib/langStore";
import { translations } from "@/lib/i18n";
import Layout from "@/components/Layout";

const skills = [
  { name: "Logo Design", nameAr: "تصميم الشعارات", level: 98 },
  { name: "Brand Identity", nameAr: "الهوية التجارية", level: 95 },
  { name: "Typography", nameAr: "الخط والطباعة", level: 90 },
  { name: "Illustration", nameAr: "الرسوم التوضيحية", level: 82 },
  { name: "Motion Design", nameAr: "تصميم الحركة", level: 75 },
  { name: "Brand Strategy", nameAr: "استراتيجية العلامة", level: 88 },
];

const experience = [
  {
    period: "2022–Present",
    periodAr: "2022–الآن",
    role: "Senior Brand Designer",
    roleAr: "مصمم هوية بصرية أول",
    company: "Freelance / Studio",
    companyAr: "مستقل / ستوديو",
    desc: "Creating comprehensive brand identities for global clients across fintech, wellness, and creative industries.",
    descAr: "إنشاء هويات بصرية شاملة لعملاء عالميين في قطاعات التكنولوجيا المالية والصحة والصناعات الإبداعية.",
  },
  {
    period: "2019–2022",
    periodAr: "2019–2022",
    role: "Brand Designer",
    roleAr: "مصمم علامات تجارية",
    company: "Creative Agency",
    companyAr: "وكالة إبداعية",
    desc: "Developed visual identities for 40+ brands, specializing in logo systems and brand guidelines.",
    descAr: "طوّر هويات بصرية لأكثر من 40 علامة تجارية متخصصاً في أنظمة الشعارات وأدلة العلامات التجارية.",
  },
  {
    period: "2017–2019",
    periodAr: "2017–2019",
    role: "Junior Designer",
    roleAr: "مصمم مبتدئ",
    company: "Design Studio",
    companyAr: "استوديو التصميم",
    desc: "Supported senior designers in logo creation, print, and digital asset production.",
    descAr: "دعم المصممين الأوائل في إنشاء الشعارات وإنتاج الأصول المطبوعة والرقمية.",
  },
];

const awards = [
  { year: "2025", title: "Brand Identity of the Year", titleAr: "هوية بصرية العام", org: "Awwwards" },
  { year: "2024", title: "Logo Design Excellence", titleAr: "تميز تصميم الشعار", org: "Behance" },
  { year: "2023", title: "Best Visual Identity", titleAr: "أفضل هوية بصرية", org: "Dribbble Awards" },
  { year: "2022", title: "Top Logo Designer", titleAr: "أفضل مصمم شعارات", org: "99designs" },
];

export default function AboutPage() {
  const { lang } = useLangStore();
  const t = translations[lang];

  return (
    <Layout>
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-2">✦ {t.about.title}</p>
              <h1 className="text-4xl lg:text-5xl font-extrabold text-foreground mb-6 leading-tight">
                {t.about.subtitle}
              </h1>
              <p className="text-muted-foreground leading-relaxed mb-4 text-lg">{t.about.bio1}</p>
              <p className="text-muted-foreground leading-relaxed mb-8">{t.about.bio2}</p>
              <button className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 active:scale-95 transition-all duration-200 shadow-lg shadow-primary/25">
                <Download size={16} />
                {t.about.downloadCV}
              </button>
            </motion.div>

            {/* Avatar / Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center"
            >
              <motion.div 
                animate={{
                  y: [0, -20, 0],
                  rotate: [6, 4, 6],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative w-72 h-72 lg:w-80 lg:h-80"
              >
                <div
                  className="absolute inset-0 rounded-3xl"
                  style={{
                    background:
                      "linear-gradient(135deg, oklch(0.55 0.22 255 / 0.3), oklch(0.72 0.20 40 / 0.3))",
                  }}
                />
                <div className="absolute inset-0 rounded-3xl border border-primary/30 bg-card flex items-center justify-center overflow-hidden shadow-2xl shadow-primary/10">
                  <img src="/images/work2.jpg" alt="Work 2" className="w-full h-full object-cover" />
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Skills */}
          <div className="mb-20">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl font-extrabold text-foreground mb-8"
            >
              {t.about.skillsTitle}
            </motion.h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {skills.map((skill, i) => (
                <motion.div
                  key={skill.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                >
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-foreground">
                      {lang === "ar" ? skill.nameAr : skill.name}
                    </span>
                    <span className="text-primary font-bold">{skill.level}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${skill.level}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: i * 0.08, ease: "easeOut" }}
                      className="h-full rounded-full bg-primary"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Experience & Awards grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Experience */}
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl font-extrabold text-foreground mb-8"
              >
                {t.about.experienceTitle}
              </motion.h2>
              <div className="space-y-6">
                {experience.map((exp, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="relative ps-6 border-s-2 border-border"
                  >
                    <div className="absolute start-[-5px] top-1.5 w-2.5 h-2.5 rounded-full bg-primary border-2 border-background" />
                    <div className="text-xs text-primary font-mono font-bold mb-1">
                      {lang === "ar" ? exp.periodAr : exp.period}
                    </div>
                    <div className="font-bold text-foreground">{lang === "ar" ? exp.roleAr : exp.role}</div>
                    <div className="text-sm text-muted-foreground mb-1">
                      {lang === "ar" ? exp.companyAr : exp.company}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {lang === "ar" ? exp.descAr : exp.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Awards */}
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl font-extrabold text-foreground mb-8"
              >
                {t.about.awardsTitle}
              </motion.h2>
              <div className="space-y-4">
                {awards.map((award, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:border-accent/40 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-extrabold text-sm shrink-0">
                      {award.year.slice(2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-foreground text-sm">
                        {lang === "ar" ? award.titleAr : award.title}
                      </div>
                      <div className="text-xs text-muted-foreground">{award.org}</div>
                    </div>
                    <div className="text-accent text-lg">★</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
